import { DownloadOutlined } from '@ant-design/icons'
import { Button, Card, Col, List, Result, Row, Typography } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { parseDate } from '../../config/dayjsConfig'
import Loading from '../../shared/components/Loading'
import { Delivery, DeliveryStatus } from '../../types/deliverySubscription/Delivery'
import { useDelivery } from '../../features/deliverySubscriptions/queries/useDelivery'
import { useDeliveries } from '../../features/deliverySubscriptions/queries/useDeliveries'
import { downloadDelivery } from '../../features/deliverySubscriptions/service/deliverySubscriptionService'
import { showErrorMessage } from '../../utils/message'
import { autoDownload } from '../../utils/helpers'

const DeliveryDownload = () => {
  const { t } = useTranslation()
  const { uuid } = useParams() as { uuid: string }

  const { data: delivery, isLoading } = useDelivery(uuid)
  const { data: relatedDeliveries = [] } = useDeliveries(delivery?.contractId, delivery?.deliverySubscriptionId)

  const download = async (id: string) => {
    try {
      const response = await downloadDelivery(id)
      autoDownload(`${id}.pdf`, response)
    } catch {
      showErrorMessage()
    }
  }

  const latestDeliveries = relatedDeliveries
    .filter(d => d.uuid && d.id !== delivery?.id && d.status === DeliveryStatus.FINISHED)
    .sort((a, b) => (parseDate(b?.endTime)?.valueOf() || 0) - (parseDate(a?.endTime)?.valueOf() || 0))

  if (isLoading) return <Loading />

  return (
    <Card>
      {delivery ? (
        <>
          <Result
            status="success"
            title={t('subscriptionsPage:downloadTitle')}
            subTitle={parseDate(delivery.endTime)?.format('lll')}
            extra={[
              <Button key="dl" icon={<DownloadOutlined />} onClick={() => download(delivery.uuid)}>
                {t('global:download')}
              </Button>
            ]}
          />
          {!!latestDeliveries.length && (
            <List
              header={<h3>{t('subscriptionsPage:latestDeliveries')}</h3>}
              itemLayout="horizontal"
              dataSource={latestDeliveries}
              renderItem={(item: Delivery) => (
                <List.Item>
                  <List.Item.Meta
                    title={
                      <Row>
                        <Col span={4}>{parseDate(item.endTime)?.format('lll')}</Col>
                        {item.meta?.periodGroups?.[0].endDate && (
                          <Col span={20}>
                            <Typography.Text type="secondary">
                              {parseDate(item.meta.periodGroups[0].endDate)?.format('MM/YYYY')}
                            </Typography.Text>
                          </Col>
                        )}
                      </Row>
                    }
                  />
                  <Button icon={<DownloadOutlined />} type="link" onClick={() => download(item.uuid)}>
                    {t('global:download')}
                  </Button>
                </List.Item>
              )}
            />
          )}
        </>
      ) : (
        <Result
          status="warning"
          title={t('subscriptionsPage:fileNotFound')}
          subTitle={t('description:CONTACT_SUPPORT')}
        />
      )}
    </Card>
  )
}

export default DeliveryDownload
